import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { WrenchIcon, PenToolIcon, BookIcon, UsersIcon, LightbulbIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, ClipboardIcon, MessageSquareIcon, HelpCircleIcon, CalendarIcon, StarIcon, ClockIcon, BadgeCheckIcon } from 'lucide-react';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { useDarkMode } from '../../context/DarkModeContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import ArticleCard from '../../components/Articles/ArticleCard';
// --- Availability Data ---
const availabilityProviders = [{
  id: 1,
  name: 'Wordsmith Editorial',
  type: 'Developmental Editor',
  specialty: 'Romance & Fantasy',
  slots: 3,
  turnaround: '4–6 weeks',
  price: '$800–$1,500',
  description: 'Specialising in romance and fantasy with 10+ years experience. Free 30-min consultation included.',
  isNew: true,
  borderColor: 'border-purple-500',
  badgeColor: 'bg-purple-100 text-purple-700',
  badgeDark: 'bg-purple-900/40 text-purple-300'
}, {
  id: 2,
  name: 'Luna Design Co.',
  type: 'Cover Designer',
  specialty: 'Contemporary & Paranormal Romance',
  slots: 5,
  turnaround: '3–4 weeks',
  price: '$350–$800',
  description: 'Award-winning cover design for indie romance authors. Portfolio available on request.',
  isNew: true,
  borderColor: 'border-pink-500',
  badgeColor: 'bg-pink-100 text-pink-700',
  badgeDark: 'bg-pink-900/40 text-pink-300'
}, {
  id: 3,
  name: 'FormatPro Services',
  type: 'Interior Formatter',
  specialty: 'Print & Ebook',
  slots: 8,
  turnaround: '5–7 days',
  price: '$150–$400',
  description: 'Professional interior formatting for print and ebook. Launch pricing available until Nov 1st.',
  isNew: false,
  borderColor: 'border-green-500',
  badgeColor: 'bg-green-100 text-green-700',
  badgeDark: 'bg-green-900/40 text-green-300'
}, {
  id: 4,
  name: 'Clear Voice Narration',
  type: 'Audiobook Narrator',
  specialty: 'Fantasy & Sci-Fi',
  slots: 2,
  turnaround: '6–8 weeks',
  price: '$200–$400/hr',
  description: 'SAG-AFTRA narrator with 50+ audiobooks. Accepting new projects for Q1 2024.',
  isNew: false,
  borderColor: 'border-blue-500',
  badgeColor: 'bg-blue-100 text-blue-700',
  badgeDark: 'bg-blue-900/40 text-blue-300'
}, {
  id: 5,
  name: 'Inkwell Copy Editing',
  type: 'Copy Editor',
  specialty: 'All Genres',
  slots: 4,
  turnaround: '2–3 weeks',
  price: '$400–$900',
  description: 'Meticulous copy editing with a light touch. Chicago Manual of Style specialist.',
  isNew: true,
  borderColor: 'border-indigo-500',
  badgeColor: 'bg-indigo-100 text-indigo-700',
  badgeDark: 'bg-indigo-900/40 text-indigo-300'
}, {
  id: 6,
  name: 'StoryArc Marketing',
  type: 'Book Marketer',
  specialty: 'Indie Romance & Fantasy',
  slots: 2,
  turnaround: '2–4 weeks',
  price: '$500–$1,200',
  description: 'Full-service book launch marketing including ARC coordination, social strategy, and newsletter campaigns.',
  isNew: false,
  borderColor: 'border-amber-500',
  badgeColor: 'bg-amber-100 text-amber-700',
  badgeDark: 'bg-amber-900/40 text-amber-300'
}];
// --- Self-Editing Articles ---
const selfEditingArticles = [{
  id: 1,
  title: 'The 10-Point Self-Edit Checklist Every Author Needs',
  excerpt: 'Before you send your manuscript to a professional editor, run through this checklist to catch the most common issues that slow down the editing process.',
  author: 'Maya Thornton, Editor',
  date: 'October 14, 2023',
  category: 'Self-Editing',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&q=80'
}, {
  id: 2,
  title: "Show Don't Tell: A Practical Guide for Fiction Writers",
  excerpt: 'The most cited piece of writing advice, finally broken down with concrete before-and-after examples you can apply immediately.',
  author: 'James Whitfield, Developmental Editor',
  date: 'October 10, 2023',
  category: 'Manuscript Tips',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=800&q=80'
}, {
  id: 3,
  title: 'How to Identify and Fix Pacing Problems in Your Novel',
  excerpt: 'Pacing issues are the #1 reason manuscripts get rejected. Learn to diagnose slow scenes, rushed climaxes, and uneven tension arcs.',
  author: 'Dr. Sarah Chen',
  date: 'October 5, 2023',
  category: 'Story Structure',
  readTime: '11 min read',
  image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80'
}, {
  id: 4,
  title: 'Line Editing Your Own Work: Techniques the Pros Use',
  excerpt: 'Professional copy editors use specific techniques to distance themselves from their own prose. Here is how to apply them to your own manuscript.',
  author: 'Lena Park, Copy Editor',
  date: 'September 30, 2023',
  category: 'Line Editing',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=800&q=80'
}];
// --- Help Resources ---
const helpResources = [{
  icon: CheckCircleIcon,
  iconColor: 'text-green-500',
  iconBg: 'bg-green-100',
  iconBgDark: 'bg-green-900/30',
  title: 'What to Expect from a Developmental Edit',
  description: 'A clear breakdown of what developmental editors look for, what feedback looks like, and how to use it effectively.'
}, {
  icon: ClipboardIcon,
  iconColor: 'text-blue-500',
  iconBg: 'bg-blue-100',
  iconBgDark: 'bg-blue-900/30',
  title: 'How to Prepare Your Manuscript for an Editor',
  description: 'Formatting requirements, style sheets, and the information your editor needs before they start work.'
}, {
  icon: MessageSquareIcon,
  iconColor: 'text-purple-500',
  iconBg: 'bg-purple-100',
  iconBgDark: 'bg-purple-900/30',
  title: 'Communicating with Your Service Provider',
  description: 'Best practices for briefs, revision rounds, feedback, and maintaining a productive working relationship.'
}];
const ServicesTrove = () => {
  const {
    darkMode
  } = useDarkMode();
  const {
    isReader
  } = useUserRole();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  return <TroveLayout sidebarProps={{
    showServiceCategories: true
  }} data-id="element-16627" data-mp="1vakiow67yfbf">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4" data-id="element-16628" data-mp="2095k2yx7j51g">
        <WrenchIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-16629" data-mp="3pntnwbrqfenv" />
        <Link to="/trove" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16630" data-mp="3plh5xylz54at">
          The Trove
        </Link>
        <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16631" data-mp="3ukajfr19gvaz" />
        <span className={`text-sm ${dk.textMuted}`} data-id="element-16632" data-mp="1izt7ydrs2e0v">Services Hub</span>
      </div>

      {/* Header */}
      <div className="mb-10" data-id="element-16633" data-mp="1e1rrm0lqkst3">
        <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${dk.textPrimary}`} data-id="element-16634" data-mp="1dydpmtgzilm6">
          Services Hub
        </h1>
        <p className={`text-base max-w-2xl ${dk.textMuted}`} data-id="element-16635" data-mp="1nyo0i7fjrgy6">
          Find service providers with open availability, read expert
          self-editing tips from working editors, and get help navigating the
          world of publishing services.
        </p>
      </div>

      <div className="space-y-14" data-id="element-16636" data-mp="1wt2e85kfijf6">
        {/* ── Section 1: Open for Bookings ── */}
        <section data-id="element-16637" data-mp="2bn778iiy7lo5">
          <div className="flex items-center justify-between mb-5" data-id="element-16638" data-mp="2blmcarsq20pr">
            <div className="flex items-center gap-2" data-id="element-16639" data-mp="26pf9v8zjb7ck">
              <CalendarIcon className="h-5 w-5 text-green-500" data-id="element-16640" data-mp="h9fsx5pocdmd" />
              <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-16641" data-mp="hbsj265l5uai">
                Open for Bookings
              </h2>
            </div>
            <Link to="/trove/services/listings" className={`text-sm font-medium flex items-center gap-1 ${rt.accentLink}`} data-id="element-16642" data-mp="3mnuyor10fk8r">
              View all providers <ArrowRightIcon className="h-4 w-4" data-id="element-16643" data-mp="6yujv01cqeo" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16644" data-mp="56lhwx9kvn17">
            {availabilityProviders.map(provider => <div key={provider.id} className={`rounded-xl border-l-4 ${provider.borderColor} p-5 ${dk.card} shadow-sm`} data-id="element-16645" data-mp="33p0kbj5gklt4">
                <div className="flex items-start justify-between gap-3 mb-2" data-id="element-16646" data-mp="3dmeoaja73i4j">
                  <div data-id="element-16647" data-mp="3dohofw52armz">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-16648" data-mp="3ho05uhxockl2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${darkMode ? provider.badgeDark : provider.badgeColor}`} data-id="element-16649" data-mp="3hlvu5dtjaha8">
                        {provider.type}
                      </span>
                      {provider.isNew && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-id="element-16650" data-mp="2hhmpwfaffzi4">
                          New
                        </span>}
                    </div>
                    <h3 className={`text-base font-bold mt-2 ${dk.textPrimary}`} data-id="element-16651" data-mp="23nvq3ymhztzl">
                      {provider.name}
                    </h3>
                    <p className={`text-xs mt-0.5 ${dk.textMuted}`} data-id="element-16652" data-mp="1tpg0k034css7">
                      {provider.specialty}
                    </p>
                  </div>
                  <div className={`text-right shrink-0`} data-id="element-16653" data-mp="1kpsxl9uqrex1">
                    <p className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-16654" data-mp="1krdperpo8jr7">
                      {provider.price}
                    </p>
                    <p className={`text-xs mt-0.5 ${dk.textMuted}`} data-id="element-16655" data-mp="3f3tw1tmdpzwl">
                      {provider.slots} slot{provider.slots !== 1 ? 's' : ''}{' '}
                      left
                    </p>
                  </div>
                </div>
                <p className={`text-sm mb-3 ${dk.textSecondary}`} data-id="element-16656" data-mp="1y29v0astd3wv">
                  {provider.description}
                </p>
                <div className="flex items-center justify-between" data-id="element-16657" data-mp="2c06v3h6h7sga">
                  <div className="flex items-center gap-1" data-id="element-16658" data-mp="273zmthqly4v6">
                    <ClockIcon className={`h-3.5 w-3.5 ${dk.textMuted}`} data-id="element-16659" data-mp="1jddrmr1pcf2l" />
                    <span className={`text-xs ${dk.textMuted}`} data-id="element-16660" data-mp="1fcaam17x4czs">
                      {provider.turnaround}
                    </span>
                  </div>
                  <button className={`text-xs font-medium ${rt.accentLink}`} data-id="element-16661" data-mp="1t59oxzvys15r">
                    View Profile →
                  </button>
                </div>
              </div>)}
          </div>
        </section>

        {/* ── Section 2: Self-Editing Tips from the Pros ── */}
        <section data-id="element-16662" data-mp="vnjd851qiig8">
          <div className="flex items-center justify-between mb-2" data-id="element-16663" data-mp="vo17iw4u6mli">
            <div className="flex items-center gap-2" data-id="element-16664" data-mp="37alidk1d49d0">
              <LightbulbIcon className="h-5 w-5 text-amber-500" data-id="element-16665" data-mp="379tpigz5gpqf" />
              <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-16666" data-mp="3c3xskp5aodm8">
                Self-Editing Tips from the Pros
              </h2>
            </div>
            <Link to="/trove/services/articles" className={`text-sm font-medium flex items-center gap-1 ${rt.accentLink}`} data-id="element-16667" data-mp="339jgevg29j7c">
              View all articles <ArrowRightIcon className="h-4 w-4" data-id="element-16668" data-mp="3v18w4lxylt7h" />
            </Link>
          </div>
          <p className={`text-sm mb-5 ${dk.textMuted}`} data-id="element-16669" data-mp="3uywc838n58rs">
            Editors share their insider knowledge to help authors polish their
            manuscripts before submission.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-16670" data-mp="3m2nb8k50m23j">
            {selfEditingArticles.map(article => <ArticleCard key={article.id} article={article} data-id="element-16671" data-mp="2alx3uxouv48c" />)}
          </div>
        </section>

        {/* ── Section 3: Help & Resources ── */}
        <section data-id="element-16672" data-mp="1qldkov4sfig0">
          <div className="flex items-center gap-2 mb-5" data-id="element-16673" data-mp="1mhnz8ejetgz7">
            <HelpCircleIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-16674" data-mp="1mi7ji1wj8hge" />
            <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-16675" data-mp="1hq5zftt4hmft">
              Working with Service Providers
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-16676" data-mp="1cps1j4l8jsvi">
            {helpResources.map(res => <div key={res.title} className={`rounded-xl border p-6 flex flex-col gap-4 ${dk.card}`} data-id="element-16677" data-mp="17qf79ys1bjiw">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? res.iconBgDark : res.iconBg}`} data-id="element-16678" data-mp="yswf8afz44xi">
                  <res.icon className={`h-5 w-5 ${res.iconColor}`} data-id="element-16679" data-mp="2t5uqoxdet7mf" />
                </div>
                <div data-id="element-16680" data-mp="2o7azoijyl5n7">
                  <h3 className={`text-sm font-bold mb-1.5 ${dk.textPrimary}`} data-id="element-16681" data-mp="2o6rum2feqbql">
                    {res.title}
                  </h3>
                  <p className={`text-xs leading-relaxed ${dk.textMuted}`} data-id="element-16682" data-mp="2vqahwkvoor4z">
                    {res.description}
                  </p>
                </div>
                <button className={`text-xs font-medium mt-auto ${rt.accentLink}`} data-id="element-16683" data-mp="35fu4o1mip554">
                  Learn more →
                </button>
              </div>)}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className={`rounded-2xl p-8 text-center ${darkMode ? 'bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border border-purple-800/50' : 'bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100'}`} data-id="element-16684" data-mp="2cu18iavq8orb">
          <BadgeCheckIcon className={`h-8 w-8 mx-auto mb-3 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} data-id="element-16685" data-mp="2qs8zm2630vuk" />
          <h2 className={`text-2xl font-bold mb-3 ${dk.textPrimary}`} data-id="element-16686" data-mp="21q157yyyhes">
            Are you a service provider?
          </h2>
          <p className={`max-w-xl mx-auto mb-6 text-sm ${dk.textSecondary}`} data-id="element-16687" data-mp="4jtccnv1q0mx">
            Join our directory to showcase your portfolio, post availability
            updates, and connect with authors looking for your expertise.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-16688" data-mp="ej0tlm4wiffb">
            <Link to="/create-vendor-account" className={`px-6 py-2.5 rounded-lg font-medium text-white shadow-sm transition-colors ${isReader ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-purple-600 hover:bg-purple-700'}`} data-id="element-16689" data-mp="9i4qvbwaczbh">
              Create Vendor Profile
            </Link>
            <Link to="/help" className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${darkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-16690" data-mp="x87970aeeqww">
              Learn More
            </Link>
          </div>
        </section>
      </div>
    </TroveLayout>;
};
export default ServicesTrove;